"use client"

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import Link from "next/link"

import { useTestSetMetadataTransport } from "@/test-set/transport"
import { Button, Card } from "@coval/ui"
import { cn } from "@coval/ui/cn"
import { Input } from "@coval/ui/input"
import { AlertCircle, AlertTriangle, Check, Loader2, X } from "lucide-react"

import { CreateResourceButton } from "@/components/ResourceActionButton"

import type { ActionExecutionResult } from "../hooks/useActionExecutor"
import {
  useCoviAnalytics,
  type CoviActionConfirmationVia,
} from "../hooks/useCoviAnalytics"
import type {
  CoviActionItem,
  CoviActionItemPatch,
  SofiaProposedAction,
} from "../model/types"
import {
  EXPIRED_CONFIRMATION_MESSAGE,
  runConfirmedAction,
} from "../utils/actionExecution"
import { navigateDestinationLabel } from "../utils/actionRoutes"
import {
  buildActionTitle,
  buildMutationLabel,
  isAutoExecute,
  isDestructive,
  isModify,
  isServerExecuted,
  summarizeActionArgs,
} from "../utils/actions"
import { getCreationResource } from "../utils/creationActions"
import {
  INVALID_METRIC_PROPOSAL_MESSAGE,
  parseMetricCreateArgs,
} from "../utils/metricCreateActionArgs"
import { titleCase } from "../utils/titleCase"
import { ActionCardFeedback } from "./ActionCardFeedback"
import { CreationActionSummary } from "./CreationActionSummary"
import { DraftTestCasesSummary } from "./DraftTestCasesSummary"
import { PythonMetricActionDetails } from "./PythonMetricActionDetails"

const DEFAULT_EXECUTION_ERROR = "The action failed unexpectedly."

/**
 * Proposals older than this never auto-execute — protects against hydrated
 * (pre-reload) proposals firing the moment the panel reopens.
 */
const AUTO_EXECUTE_FRESHNESS_MS = 15_000

interface ConfirmActionCardProps {
  item: CoviActionItem
  execute: (proposal: SofiaProposedAction) => Promise<ActionExecutionResult>
  update: (patch: CoviActionItemPatch) => void
  disabled?: boolean
  inStack?: boolean
  nestedDraft?: boolean
  children?: ReactNode
  hasPendingTestCases?: boolean
  confirmName?: string
  confirmNameError?: boolean
  /** True when the open test-set editor will stage this action locally. */
  stagedEditorAction?: boolean
  /**
   * Asks Sofia, in-panel, to re-propose this action after its token lapsed.
   * Returns false when the thread cannot take the turn (a send is streaming),
   * so the card is not settled on a click that went nowhere.
   */
  onRepropose?: (proposal: SofiaProposedAction) => boolean
}

/**
 * One of Sofia's proposed actions, rendered under the assistant message.
 *
 * Every write is manual: non-destructive actions execute from the explicit
 * Confirm click, and destructive actions keep a two-step arm-and-execute gate
 * (or typed confirmation for test-set deletion). Only the no-cost kinds
 * `isAutoExecute` allows — and a staged test-set editor preview — run on their
 * own. Outcomes persist into the thread via `update` (the parent patches the
 * stored item).
 */
export function ConfirmActionCard({
  item,
  execute,
  update,
  disabled = false,
  inStack = false,
  nestedDraft = false,
  children,
  hasPendingTestCases = false,
  confirmName,
  confirmNameError,
  stagedEditorAction = false,
  onRepropose,
}: ConfirmActionCardProps) {
  const [armed, setArmed] = useState(false)
  // Re-entry guard: status flips through `update` are async re-renders, so a
  // double-click could otherwise fire the mutation twice.
  const executingRef = useRef(false)

  const analytics = useCoviAnalytics()

  const { proposal } = item
  const invalidMetric =
    proposal.kind === "create_metric" && !parseMetricCreateArgs(proposal).ok
  const creation = getCreationResource(proposal)
  const title = buildActionTitle(proposal)
  const mutationLabel = buildMutationLabel(proposal)
  const argRows = summarizeActionArgs(proposal)
  const argumentTable = (
    <table className="mt-2 w-full text-[11px] text-coval-text-muted">
      <tbody>
        {argRows.map((row) => (
          <tr key={row.key}>
            <td className="whitespace-nowrap pr-3 align-top font-medium">
              {row.key}
            </td>
            <td className="break-all">{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
  const serverExecuted = isServerExecuted(proposal.kind) && !stagedEditorAction
  // Destructive (delete_*) cards get danger styling + consequence-specific
  // warning copy; modify (update_*) cards get a "what changes" treatment.
  // Persisted writes require manual confirmation. The one exception is an
  // active test-set editor preview: staging is reversible, and saving the test
  // set is its review gate.
  const destructive = isDestructive(proposal.kind)
  const modify = isModify(proposal.kind)

  // A card can lapse before it is ever clicked (hydrated from storage, or left
  // open past the window). Offering Confirm on one only spends a round trip on
  // a guaranteed rejection, so treat it as expired up front.
  const expiresAtMs =
    typeof proposal.expires_at === "number" ? proposal.expires_at * 1000 : null
  // An idle open panel is a normal state and produces no renders, so the expiry
  // has to wake the card itself — otherwise a card that was fresh when it
  // rendered keeps its Confirm button long after the token died.
  const [clockedAt, setClockedAt] = useState(() => Date.now())
  useEffect(() => {
    if (expiresAtMs === null || item.status !== "proposed") {
      return
    }
    const remaining = expiresAtMs - Date.now()
    if (remaining <= 0) {
      return
    }
    const timer = setTimeout(() => setClockedAt(Date.now()), remaining)
    return () => clearTimeout(timer)
  }, [expiresAtMs, item.status])
  const lapsed = expiresAtMs !== null && clockedAt >= expiresAtMs
  const expired =
    item.status === "error"
      ? item.expired === true
      : lapsed && !stagedEditorAction
  const canRepropose = expired && !!onRepropose && item.status !== "executing"

  const [confirmText, setConfirmText] = useState("")
  const requiresTypedConfirm = proposal.kind === "delete_test_set"
  const typedConfirmed = !!confirmName && confirmText.trim() === confirmName

  const runExecution = useCallback(
    async (via: CoviActionConfirmationVia) => {
      if (disabled || invalidMetric || executingRef.current) {
        return
      }
      executingRef.current = true
      setArmed(false)
      try {
        await runConfirmedAction({
          proposal,
          via,
          serverExecuted,
          execute,
          update,
          analytics,
        })
      } finally {
        executingRef.current = false
      }
    },
    [
      analytics,
      disabled,
      execute,
      invalidMetric,
      proposal,
      serverExecuted,
      update,
    ],
  )

  // The parent passes a fresh `update` closure on every panel render, which
  // churns runExecution's identity. The auto-execute effect must NOT re-fire on
  // that churn, so it reads the latest execution through a ref.
  const runExecutionRef = useRef(runExecution)
  useEffect(() => {
    runExecutionRef.current = runExecution
  }, [runExecution])

  // Navigation and reversible editor previews run the instant they arrive.
  // A staged test-set action does not persist anything: saving the editor is
  // its review gate. The freshness guard prevents a hydrated proposal from
  // being staged again when the panel reopens.
  const autoExecuteActive =
    item.status === "proposed" &&
    (isAutoExecute(proposal.kind) || stagedEditorAction) &&
    Date.now() - item.proposedAt < AUTO_EXECUTE_FRESHNESS_MS

  useEffect(() => {
    if (!autoExecuteActive) {
      return
    }
    void runExecutionRef.current("auto_execute")
  }, [autoExecuteActive])

  const isNavigate = proposal.kind === "navigate"

  // Navigate auto-executes on arrival — it never renders the confirm card.
  // While it is firing ("proposed"/"executing") render nothing (the push is
  // effectively instant); a rare failure renders a compact inline error.
  if (isNavigate && item.status !== "done") {
    if (item.status === "error") {
      return (
        <div className="flex items-start gap-1.5 pl-9 text-xs text-coval-danger-text">
          <AlertCircle
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            aria-hidden="true"
          />
          <span>{item.error ?? DEFAULT_EXECUTION_ERROR}</span>
        </div>
      )
    }
    return null
  }

  if (creation && (item.status === "done" || item.status === "dismissed")) {
    return (
      <Card className={cn("min-w-0 space-y-4 p-4", !inStack && "ml-9")}>
        <CreationActionSummary
          proposal={proposal}
          status={item.status}
          withTestCases={!!children}
        />
        {proposal.kind === "create_metric" &&
        item.status === "done" &&
        item.detail ? (
          <p className="break-words text-sm text-coval-text-muted">
            <span className="sr-only">Metric creation receipt: </span>
            {item.detail}
          </p>
        ) : null}
        <ActionCardFeedback item={item} hideExplanation={!!children} />
        {children}
        {item.status === "done" && item.href && !hasPendingTestCases ? (
          <Button asChild variant="secondary">
            <Link href={item.href}>
              {children ? "Open Editor" : `Go to ${creation.label}`}
            </Link>
          </Button>
        ) : null}
      </Card>
    )
  }

  if (nestedDraft && (item.status === "done" || item.status === "dismissed")) {
    return (
      <div className="space-y-3">
        <DraftTestCasesSummary proposal={proposal} status={item.status} />
        <p className="text-xs text-coval-text-muted">
          {item.status === "dismissed"
            ? "Dismissed"
            : (item.detail ?? "Test cases staged")}
        </p>
        <ActionCardFeedback item={item} />
      </div>
    )
  }

  if (item.status === "done") {
    // Navigate's receipt is a compact one-liner ("→ Took you to Runs") with no
    // "View" link (you are already there). Other kinds keep the ✓ + View link.
    if (isNavigate) {
      const destinationLabel = navigateDestinationLabel(
        typeof proposal.args.destination === "string"
          ? proposal.args.destination
          : "",
      )
      return (
        <div
          data-testid="navigate-receipt"
          className={cn("text-xs text-coval-text-muted", !inStack && "pl-9")}
        >
          <span className="text-coval-text">
            <span aria-hidden="true">→ </span>
            {item.detail ?? `Took you to ${destinationLabel}`}
          </span>
        </div>
      )
    }
    return (
      <div className={cn("text-xs text-coval-text-muted", !inStack && "pl-9")}>
        <span className="inline-flex items-center gap-1.5">
          <Check
            className="h-3 w-3 shrink-0 text-coval-success-text"
            aria-hidden="true"
          />
          <span className="text-coval-text">
            {title}
            {item.detail ? ` — ${item.detail}` : null}
          </span>
          {item.href ? (
            <Link href={item.href} className="text-coval-info-text underline">
              {proposal.kind === "create_metric" ||
              proposal.kind === "publish_python_metric" ||
              proposal.kind === "save_python_analysis_metric"
                ? "Go to metric"
                : "View"}
            </Link>
          ) : null}
        </span>
        <ActionCardFeedback item={item} />
      </div>
    )
  }

  if (item.status === "dismissed") {
    return (
      <div className={cn("text-xs text-coval-text-muted", !inStack && "pl-9")}>
        <span className="inline-flex items-center gap-1.5">
          <X className="h-3 w-3 shrink-0" aria-hidden="true" />
          Dismissed — {title}
        </span>
      </div>
    )
  }

  return (
    <Card
      data-testid="confirm-action-card"
      data-destructive={destructive ? "true" : undefined}
      data-modify={modify ? "true" : undefined}
      className={cn(
        "min-w-0 px-3 py-2.5",
        nestedDraft && "border-0 bg-transparent p-0 shadow-none",
        !inStack && "ml-9",
        creation && "p-4",
        // Destructive cards always wear danger colors so they never look like an
        // ordinary confirm; arming only shifts the active card's border.
        destructive
          ? "border-coval-danger-border bg-coval-danger-surface"
          : cn(
              creation
                ? "bg-surface-default"
                : nestedDraft
                  ? "bg-transparent"
                  : "bg-surface-1",
              armed ? "border-coval-border-active" : "border-coval-border",
            ),
      )}
    >
      {creation ? (
        <CreationActionSummary proposal={proposal} />
      ) : nestedDraft ? (
        <DraftTestCasesSummary proposal={proposal} status={item.status} />
      ) : (
        <div
          className={cn(
            "flex items-center gap-1.5 text-sm font-medium",
            destructive ? "text-coval-danger-on-surface" : "text-coval-text",
          )}
        >
          {destructive ? (
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : null}
          <span>{title}</span>
        </div>
      )}

      {destructive ? (
        <div className="mt-1 text-xs font-medium text-coval-danger-on-surface">
          {stagedEditorAction
            ? "This stages the removal in the editor. You can Undo it before saving the test set."
            : proposal.kind === "cancel_run"
              ? "This stops the run. It can't be resumed — only launched again."
              : proposal.kind === "delete_agent"
                ? "This removes the agent from active use. It is soft-deleted and can be restored."
                : proposal.kind === "delete_alert"
                  ? "This removes the alert from active use and stops its future notifications. It cannot be restored here."
                  : "This permanently deletes it and can't be undone."}
        </div>
      ) : null}

      {stagedEditorAction && !destructive ? (
        <div className="mt-1 text-xs text-coval-text-muted">
          This stages the change in the test-set editor. Review the diff, then
          save the test set to apply it — or Undo it.
        </div>
      ) : null}

      {!creation && !nestedDraft ? (
        <div
          className={cn(
            "mt-1 text-xs",
            destructive
              ? "text-coval-danger-on-surface"
              : "text-coval-text-muted",
            modify ? "font-medium text-coval-text" : null,
          )}
        >
          {proposal.rationale}
        </div>
      ) : null}

      <PythonMetricActionDetails proposal={proposal} />

      {modify ? (
        <div className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-coval-text-muted">
          What changes
        </div>
      ) : null}

      {/* Delete cards carry only the id, so the summary table is noise — skip it.
          Modify cards keep it: it shows the NEW values being written. */}
      {!creation && !nestedDraft && !destructive && argRows.length > 0
        ? argumentTable
        : null}

      {invalidMetric ? (
        <p className="mt-2 text-xs text-coval-text-muted">
          {INVALID_METRIC_PROPOSAL_MESSAGE}
        </p>
      ) : null}

      {expired && item.status !== "error" ? (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-coval-text-muted">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{EXPIRED_CONFIRMATION_MESSAGE}</span>
        </div>
      ) : null}

      {item.status === "error" ? (
        <div
          className={cn(
            "mt-2 flex items-start gap-1.5 text-xs",
            destructive
              ? "text-coval-danger-on-surface"
              : "text-coval-danger-text",
          )}
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {item.error}
            {item.href ? (
              <>
                {" "}
                <Link href={item.href} className="underline">
                  Open
                </Link>
              </>
            ) : null}
          </span>
        </div>
      ) : null}

      <ActionCardFeedback item={item} />

      {requiresTypedConfirm ? (
        <div className="mt-2 space-y-1">
          <Input
            aria-label="Type test set name to confirm deletion"
            placeholder="Type the test set name to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={!confirmName}
          />
          {confirmNameError ? (
            <p className="text-xs text-coval-danger-on-surface">
              Couldn&apos;t load the test set name — delete it from the test set
              page instead.
            </p>
          ) : !confirmName ? (
            <p className="text-xs text-coval-text-muted">
              Loading the test set name…
            </p>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn("flex items-center gap-3", creation ? "mt-4" : "mt-2.5")}
      >
        {nestedDraft && !expired ? (
          <>
            <Button
              disabled={disabled}
              loading={item.status === "executing"}
              onClick={() => void runExecution("manual")}
            >
              {item.status === "error"
                ? "Retry Review in Editor"
                : "Review in Editor"}
            </Button>
            <Button
              variant="ghost"
              disabled={disabled || item.status === "executing"}
              onClick={() => {
                analytics.trackActionDismissed(proposal.kind)
                update({ status: "dismissed" })
              }}
            >
              Dismiss
            </Button>
          </>
        ) : creation && !expired && !invalidMetric ? (
          <>
            {item.status === "error" ? (
              <Button
                disabled={disabled}
                onClick={() => void runExecution("manual")}
              >
                Retry
              </Button>
            ) : (
              <CreateResourceButton
                resourceName={titleCase(creation.label)}
                disabled={disabled}
                loading={item.status === "executing"}
                onClick={() => void runExecution("manual")}
              />
            )}
            <Button
              variant="ghost"
              disabled={disabled || item.status === "executing"}
              onClick={() => {
                analytics.trackActionDismissed(proposal.kind)
                update({ status: "dismissed" })
              }}
            >
              Dismiss
            </Button>
          </>
        ) : (
          <>
            {item.status === "executing" ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-coval-text-muted">
                <Loader2
                  className="h-3.5 w-3.5 animate-spin"
                  aria-hidden="true"
                />
                Running {mutationLabel}…
              </span>
            ) : (
              <>
                {/* Retrying an expired confirmation resends the same stale signed token and
                fails deterministically — the only way forward is a fresh proposal. */}
                {canRepropose ? (
                  <button
                    type="button"
                    disabled={disabled}
                    data-testid="repropose-action"
                    onClick={() => {
                      if (!onRepropose?.(proposal)) {
                        return
                      }
                      update({
                        status: "error",
                        error: EXPIRED_CONFIRMATION_MESSAGE,
                        expired: true,
                      })
                    }}
                    className="max-w-full truncate rounded-md bg-coval-contrast px-2.5 py-1 text-xs font-medium text-coval-text-on-contrast transition-colors hover:opacity-90"
                  >
                    Propose again
                  </button>
                ) : expired || invalidMetric ? null : (
                  <button
                    type="button"
                    disabled={
                      disabled || (requiresTypedConfirm && !typedConfirmed)
                    }
                    onClick={() => {
                      if (requiresTypedConfirm || !destructive || armed) {
                        void runExecution("manual")
                      } else {
                        setArmed(true)
                      }
                    }}
                    className={cn(
                      "max-w-full truncate rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50",
                      destructive
                        ? "bg-coval-danger-border text-coval-text-on-contrast hover:opacity-90"
                        : armed
                          ? "border border-coval-border-active bg-surface-default text-coval-text"
                          : "bg-coval-contrast text-coval-text-on-contrast hover:opacity-90",
                    )}
                  >
                    {armed
                      ? mutationLabel
                      : item.status === "error"
                        ? "Retry"
                        : // Destructive cards say what they do: "Delete", never the
                          // generic "Confirm".
                          destructive
                          ? "Delete"
                          : "Confirm"}
                  </button>
                )}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    analytics.trackActionDismissed(proposal.kind)
                    update({ status: "dismissed" })
                  }}
                  className={cn(
                    "text-xs underline",
                    destructive
                      ? "text-coval-danger-text hover:opacity-80"
                      : "text-coval-text-muted hover:text-coval-text",
                  )}
                >
                  Dismiss
                </button>
              </>
            )}
          </>
        )}
      </div>
    </Card>
  )
}

/**
 * Resolves the test set's display name so a delete_test_set card can gate
 * deletion behind the same type-the-name confirmation as the manual delete
 * modal. Only mounted for that kind, so other cards never touch the transport.
 */
export function DeleteTestSetConfirmCard(props: ConfirmActionCardProps) {
  const testSetId =
    typeof props.item.proposal.args.test_set_id === "string"
      ? props.item.proposal.args.test_set_id
      : ""
  const { data, isError } = useTestSetMetadataTransport(testSetId)
  return (
    <ConfirmActionCard
      {...props}
      confirmName={data?.metadata?.display_name}
      confirmNameError={isError}
    />
  )
}
